import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, User, Activity, Bell, Settings, LogOut, Crown, Star, AlertCircle, MessageSquare, HelpCircle, Globe, CreditCard, Lock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTour, TOUR_IDS } from '../contexts/TourContext';
import UpgradeModal from './UpgradeModal';

/**
 * Mobile-optimized header with hamburger navigation
 */
const MobileHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isPremium, isPro, user, logout } = useAuth();
  const { startTour } = useTour();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState('');
  const menuRef = useRef(null);
  
  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    
    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);
  
  // Close menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);
  
  // Prevent body scroll when menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);
  
  const handleLogout = async () => {
    await logout();
    setMenuOpen(false);
    navigate('/');
  };

  const handleProFeatureClick = (feature, path) => {
    if (isPro) {
      navigate(path);
      setMenuOpen(false);
    } else {
      setUpgradeFeature(feature);
      setShowUpgradeModal(true);
      setMenuOpen(false);
    }
  };

  // Main navigation items (simplified - only Dashboard)
  const mainNavItems = [
    { label: 'Dashboard', path: '/app', icon: Activity },
  ];

  // User feature items (moved from main nav)
  const userFeatureItems = [
    { label: 'Watchlist', path: '/app/watchlist', icon: Star, proOnly: true, feature: 'Watchlists' },
    { label: 'Alerts', path: '/app/alerts', icon: AlertCircle, proOnly: true, feature: 'Alerts' },
    { label: 'Settings', path: '/app/config', icon: Settings, proOnly: false },
  ];

  // General items
  const generalItems = [
    { label: 'Support', path: '/app/tickets', icon: MessageSquare },
  ];
  
  return (
    <>
      {/* Header bar */}
      <header 
        className="sticky top-0 z-50 px-4 py-3 flex items-center justify-between md:hidden"
        style={{ 
          backgroundColor: 'rgba(11, 15, 26, 0.98)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          backdropFilter: 'blur(12px)'
        }}
        data-testid="mobile-header"
      >
        {/* Logo */}
        <button 
          onClick={() => navigate('/app')}
          className="flex items-center gap-2"
        >
          <div 
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: 'rgba(59, 130, 246, 0.15)' }}
          >
            <Activity className="h-4 w-4" style={{ color: '#60A5FA' }} />
          </div>
          <span className="font-semibold text-sm" style={{ color: '#F8FAFC' }}>
            ScopeTheMarket
          </span>
        </button>
        
        {/* Right side: Profile + Hamburger */}
        <div className="flex items-center gap-2">
          {/* Pro badge (if premium) */}
          {isPremium && (
            <div 
              className="flex items-center gap-1 px-2 py-1 rounded-md"
              style={{ backgroundColor: 'rgba(139, 92, 246, 0.15)' }}
            >
              <Crown className="h-3 w-3" style={{ color: '#A78BFA' }} />
              <span className="text-xs font-medium" style={{ color: '#A78BFA' }}>Pro</span>
            </div>
          )}
          
          {/* Profile icon */}
          {isAuthenticated && (
            <button
              onClick={() => navigate('/app/settings')}
              className="w-9 h-9 rounded-full flex items-center justify-center"
              style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}
            >
              <User className="h-4 w-4" style={{ color: '#94A3B8' }} />
            </button>
          )}
          
          {/* Hamburger */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="w-9 h-9 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {menuOpen ? (
              <X className="h-5 w-5" style={{ color: '#F8FAFC' }} />
            ) : (
              <Menu className="h-5 w-5" style={{ color: '#F8FAFC' }} />
            )}
          </button>
        </div>
      </header>
      
      {/* Mobile menu overlay */}
      {menuOpen && (
        <div 
          className="fixed inset-0 z-40 md:hidden"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        >
          {/* Menu panel */}
          <div 
            ref={menuRef}
            className="absolute right-0 top-0 h-full w-72 overflow-y-auto"
            style={{ 
              backgroundColor: '#0E1324',
              borderLeft: '1px solid rgba(255,255,255,0.06)',
              animation: 'slideIn 0.2s ease-out'
            }}
          >
            {/* Menu header */}
            <div className="p-4 border-b" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
              {isAuthenticated ? (
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: 'rgba(59, 130, 246, 0.15)' }}
                  >
                    <User className="h-5 w-5" style={{ color: '#60A5FA' }} />
                  </div>
                  <div>
                    <p className="text-sm font-medium" style={{ color: '#F8FAFC' }}>
                      {user?.email || 'User'}
                    </p>
                    <p className="text-xs" style={{ color: '#64748B' }}>
                      {isPremium ? 'Pro Plan' : 'Free Plan'}
                    </p>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => { setMenuOpen(false); navigate('/login'); }}
                  className="w-full py-2.5 rounded-lg text-sm font-medium"
                  style={{ backgroundColor: '#3B82F6', color: 'white' }}
                >
                  Sign In
                </button>
              )}
            </div>
            
            {/* Main Nav items */}
            <nav className="p-2">
              {mainNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                
                return (
                  <button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors"
                    style={{ 
                      backgroundColor: isActive ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
                      color: isActive ? '#60A5FA' : '#94A3B8'
                    }}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="text-sm font-medium">{item.label}</span>
                  </button>
                );
              })}
            </nav>

            {/* Separator */}
            {isAuthenticated && (
              <div className="mx-4 h-px" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }} />
            )}

            {/* User Features Section */}
            {isAuthenticated && (
              <nav className="p-2">
                <p className="px-4 py-2 text-xs font-medium uppercase tracking-wider" style={{ color: '#64748B' }}>
                  Features
                </p>
                {userFeatureItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  const isLocked = item.proOnly && !isPro;
                  
                  return (
                    <button
                      key={item.path}
                      onClick={() => {
                        if (isLocked) {
                          handleProFeatureClick(item.feature, item.path);
                        } else {
                          navigate(item.path);
                          setMenuOpen(false);
                        }
                      }}
                      className="w-full flex items-center justify-between px-4 py-3 rounded-lg text-left transition-colors"
                      style={{ 
                        backgroundColor: isActive ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
                        color: isLocked ? '#64748B' : (isActive ? '#60A5FA' : '#94A3B8')
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <Icon className="h-5 w-5" />
                        <span className="text-sm font-medium">{item.label}</span>
                      </div>
                      {isLocked && <Lock className="h-4 w-4" />}
                    </button>
                  );
                })}
              </nav>
            )}

            {/* Separator */}
            {isAuthenticated && (
              <div className="mx-4 h-px" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }} />
            )}

            {/* General Section */}
            {isAuthenticated && (
              <nav className="p-2">
                <p className="px-4 py-2 text-xs font-medium uppercase tracking-wider" style={{ color: '#64748B' }}>
                  General
                </p>
                {generalItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  
                  return (
                    <button
                      key={item.path}
                      onClick={() => { navigate(item.path); setMenuOpen(false); }}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors"
                      style={{ 
                        backgroundColor: isActive ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
                        color: isActive ? '#60A5FA' : '#94A3B8'
                      }}
                    >
                      <Icon className="h-5 w-5" />
                      <span className="text-sm font-medium">{item.label}</span>
                    </button>
                  );
                })}
                
                {/* Start Tutorial */}
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    if (location.pathname.includes('/stock/')) {
                      startTour(TOUR_IDS.STOCK_DETAIL);
                    } else {
                      startTour(TOUR_IDS.DASHBOARD);
                    }
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors"
                  style={{ color: '#94A3B8' }}
                >
                  <HelpCircle className="h-5 w-5" />
                  <span className="text-sm font-medium">Start Tutorial</span>
                </button>
              </nav>
            )}
            
            {/* Upgrade banner (for free users) */}
            {isAuthenticated && !isPremium && (
              <div className="mx-4 my-4">
                <button
                  onClick={() => { setMenuOpen(false); navigate('/pricing'); }}
                  className="w-full p-4 rounded-xl text-left"
                  style={{ 
                    background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(59, 130, 246, 0.2) 100%)',
                    border: '1px solid rgba(139, 92, 246, 0.3)'
                  }}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Crown className="h-4 w-4" style={{ color: '#A78BFA' }} />
                    <span className="text-sm font-semibold" style={{ color: '#F8FAFC' }}>
                      Upgrade to Pro
                    </span>
                  </div>
                  <p className="text-xs" style={{ color: '#94A3B8' }}>
                    Get real-time alerts & 15-min scans
                  </p>
                </button>
              </div>
            )}

            {/* Billing (for Pro users) */}
            {isAuthenticated && isPremium && (
              <div className="px-2 py-1">
                <button
                  onClick={() => { navigate('/app/billing'); setMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors"
                  style={{ color: '#94A3B8' }}
                >
                  <CreditCard className="h-5 w-5" />
                  <span className="text-sm font-medium">Manage Subscription</span>
                </button>
              </div>
            )}
            
            {/* Logout */}
            {isAuthenticated && (
              <div className="p-4 border-t" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors hover:bg-white/5"
                  style={{ color: '#f87171' }}
                >
                  <LogOut className="h-5 w-5" />
                  <span className="text-sm font-medium">Sign Out</span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Upgrade Modal */}
      <UpgradeModal 
        isOpen={showUpgradeModal} 
        onClose={() => setShowUpgradeModal(false)}
        feature={upgradeFeature}
      />
      
      <style>{`
        @keyframes slideIn {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </>
  );
};

export default MobileHeader;
